/**
 * snapshot.selfiecam[modelName] → dashboard summary (Gemini: token counts on
 * data.response.usageMetadata).
 */

const getByPath = (obj, pathStr) => pathStr.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const pickResponseId = (data) => {
  const candidates = [
    data?.responseId,
    getByPath(data, 'response.id'),
    getByPath(data, 'response.responseId'),
    getByPath(data, 'response.prediction.id'),
    getByPath(data, 'response.data.id'),
  ];
  const first = candidates.find((c) => c != null && String(c).trim());
  return first == null ? null : String(first).trim();
};

const pickTokens = (data) => {
  const paths = [
    'spentTokens',
    'usage.total_tokens',
    'usage.totalTokens',
    'response.usage.total_tokens',
    'response.usage.totalTokens',
    'response.usageMetadata.totalTokenCount',
    'response.usageMetadata.total_token_count',
  ];
  const path = paths.find((p) => {
    const v = getByPath(data, p);
    return v != null && v !== '' && Number.isFinite(Number(v));
  });
  return path == null ? null : Number(getByPath(data, path));
};

const isPrimitive = (v) => v !== null && v !== undefined && typeof v !== 'object';

const humanizeKey = (key) => key
  .replaceAll('_', ' ')
  .replaceAll(/([A-Z])/g, ' $1')
  .replaceAll(/\s+/g, ' ')
  .trim()
  .replace(/^./, (c) => c.toUpperCase());

/** Nested leftovers → label/value rows (paths joined with ›). */
const flattenLeftover = (node, parts = []) => {
  if (node == null) return [];
  if (typeof node !== 'object') return [{ label: parts.join(' › '), value: String(node) }];
  if (Array.isArray(node)) {
    if (node.length === 0) return [{ label: parts.join(' › ') || '(root)', value: '(empty)' }];
    return node.flatMap((item, i) => flattenLeftover(item, [...parts, `[${i}]`]));
  }
  const keys = Object.keys(node);
  if (keys.length === 0) return parts.length === 0 ? [] : [{ label: parts.join(' › '), value: '{}' }];
  return keys.flatMap((k) => flattenLeftover(node[k], [...parts, k]));
};

/** [fieldKey, label] in display order, then other primitives alphabetically. */
const SUMMARY_FIELDS = [
  ['totalTokenCount', 'Total tokens'],
  ['promptTokenCount', 'Input total'],
  ['candidatesTokenCount', 'Response tokens'],
  ['thoughtsTokenCount', 'Thoughts tokens'],
];

const MODALITY_SECTIONS = [
  ['promptTokensDetails', 'Input tokens by modality'],
  ['candidatesTokensDetails', 'Response tokens by modality'],
];

const buildUsageDisplay = (usageMeta) => {
  const empty = { summary: [], modalityGroups: [], additionalRows: [] };
  if (usageMeta == null || typeof usageMeta !== 'object') return empty;

  const summary = [];
  const seen = new Set();

  SUMMARY_FIELDS.forEach(([key, label]) => {
    if (Object.hasOwn(usageMeta, key) && isPrimitive(usageMeta[key])) {
      summary.push({ key, label, value: String(usageMeta[key]) });
      seen.add(key);
    }
  });

  Object.keys(usageMeta).sort((a, b) => a.localeCompare(b)).forEach((key) => {
    if (!seen.has(key) && isPrimitive(usageMeta[key])) {
      summary.push({ key, label: humanizeKey(key), value: String(usageMeta[key]) });
      seen.add(key);
    }
  });

  const modalityGroups = [];
  MODALITY_SECTIONS.forEach(([prop, title]) => {
    const arr = usageMeta[prop];
    if (!Array.isArray(arr) || !arr.length) return;
    modalityGroups.push({
      title,
      key: prop,
      rows: arr.map((item, index) => ({
        index,
        modality: item?.modality != null ? String(item.modality) : '—',
        tokenCount: item?.tokenCount != null ? String(item.tokenCount) : '—',
      })),
    });
    seen.add(prop);
  });

  const leftover = Object.fromEntries(
    Object.keys(usageMeta).filter((k) => !seen.has(k)).map((k) => [k, usageMeta[k]]),
  );

  return {
    summary,
    modalityGroups,
    additionalRows: flattenLeftover(leftover),
  };
};

const getSelfiecamGenerationSummary = (selfiecam) => {
  if (!selfiecam || typeof selfiecam !== 'object') return null;

  const entries = Object.entries(selfiecam).filter(
    ([key, val]) => key !== 'inputs' && val && typeof val === 'object',
  );
  if (!entries.length) return null;

  const selected = entries.find(([, data]) => data?.ok === true) || entries[0];
  const [modelName, data] = selected;

  const ms = data?.processingTime;
  const usageMeta = data?.response?.usageMetadata ?? data?.usageMetadata;

  return {
    modelName,
    status: data?.ok === true ? 'Success' : 'Failed',
    processingTimeLabel: ms != null && Number.isFinite(Number(ms)) ? `${Number(ms)} ms` : '—',
    responseId: pickResponseId(data),
    tokens: pickTokens(data),
    usageDisplay: buildUsageDisplay(usageMeta),
  };
};

export default getSelfiecamGenerationSummary;
